import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="identity--role-assignment"
// Handles the group + branch selection flow for role assignments.
// When a role is checked, shows a group selector and branch checkboxes.
export default class extends Controller {
  static targets = [
    "roleBlock",
    "scopeSection",
    "roleInput",
    "groupSelect",
    "branchList",
    "branchCheckbox",
    "selectAllCheckbox",
    "roleCheckbox",
    "search",
    "filterButton",
    "visibleCount",
    "emptyState",
  ]

  #filterMode = "all"

  // Search + All/Assigned/Unassigned filter over the role boxes. Boxes are
  // hidden, never removed, so every role_assignments[] field still submits.
  setFilter(event) {
    this.#filterMode = event.currentTarget.dataset.filter
    this.filterButtonTargets.forEach((btn) => {
      const isActive = btn.dataset.filter === this.#filterMode
      btn.dataset.active = String(isActive)
      btn.setAttribute("aria-pressed", String(isActive))
    })
    this.applyFilters()
  }

  // Enter in the search box must filter, not submit the role form.
  preventSubmit(event) {
    event.preventDefault()
  }

  applyFilters() {
    const query = this.hasSearchTarget
      ? this.searchTarget.value.trim().toLowerCase()
      : ""
    let visible = 0

    this.roleBlockTargets.forEach((block) => {
      const checkbox = this.roleCheckboxTargets.find((el) => block.contains(el))
      const assigned = Boolean(checkbox?.checked)
      const matchesQuery =
        query === "" || (block.dataset.roleSearch || "").includes(query)
      const matchesMode =
        this.#filterMode === "all" ||
        (this.#filterMode === "assigned" && assigned) ||
        (this.#filterMode === "unassigned" && !assigned)
      const show = matchesQuery && matchesMode

      block.classList.toggle("hidden", !show)
      if (show) visible++
    })

    if (this.hasVisibleCountTarget)
      this.visibleCountTarget.textContent = visible
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", visible > 0)
    }
  }

  toggleRole(event) {
    const idx = event.currentTarget.dataset.roleIndex
    const checked = event.currentTarget.checked
    const scopeSection = this.scopeSectionTargets.find(
      (el) => el.dataset.roleIndex === idx,
    )
    const roleInput = this.roleInputTargets.find(
      (el) => el.dataset.roleIndex === idx,
    )
    const groupSelect = this.groupSelectTargets.find(
      (el) => el.dataset.roleIndex === idx,
    )

    if (checked) {
      scopeSection?.classList.remove("hidden")
      if (roleInput) roleInput.disabled = false
      if (groupSelect) this.#setSelectDisabled(groupSelect, false)
      this.updateBranches(idx)
    } else {
      scopeSection?.classList.add("hidden")
      if (roleInput) roleInput.disabled = true
      if (groupSelect) this.#setSelectDisabled(groupSelect, true)
      this.hideBranches(idx)
    }

    // The box may no longer belong under the active All/Assigned/Unassigned filter.
    this.applyFilters()
  }

  // The group <select> is Tom Select-enhanced (Ui::SelectComponent), so toggling
  // the native `.disabled` wouldn't update the visible control — drive the Tom
  // Select instance when present, falling back to the native select otherwise.
  #setSelectDisabled(select, disabled) {
    if (select.tomselect) {
      if (disabled) {
        select.tomselect.disable()
      } else {
        select.tomselect.enable()
      }
    } else {
      select.disabled = disabled
    }
  }

  changeGroup(event) {
    const idx = event.currentTarget.dataset.roleIndex
    this.updateBranches(idx)
  }

  toggleAllBranches(event) {
    const idx = event.currentTarget.dataset.roleIndex
    const checked = event.currentTarget.checked
    const checkboxes = this.branchCheckboxTargets.filter(
      (el) => el.dataset.roleIndex === idx && !el.closest(".hidden"),
    )
    checkboxes.forEach((cb) => {
      cb.checked = checked
    })
  }

  toggleBranch(event) {
    const idx = event.currentTarget.dataset.roleIndex
    this.syncSelectAll(idx)
  }

  updateBranches(idx) {
    const groupSelect = this.groupSelectTargets.find(
      (el) => el.dataset.roleIndex === idx,
    )
    if (!groupSelect) return

    const selectedGroupId = groupSelect.value
    const branchLists = this.branchListTargets.filter(
      (el) => el.dataset.roleIndex === idx,
    )

    branchLists.forEach((bl) => {
      if (
        bl.dataset.spaceGroupId === selectedGroupId &&
        selectedGroupId !== ""
      ) {
        bl.classList.remove("hidden")
        // Enable checkboxes in visible list
        bl.querySelectorAll("input[type=checkbox]").forEach((cb) => {
          cb.disabled = false
        })
      } else {
        bl.classList.add("hidden")
        // Disable hidden checkboxes so they are excluded from the form
        // submit, but keep their checked state so switching back to the
        // group restores the previous selection
        bl.querySelectorAll("input[type=checkbox]").forEach((cb) => {
          cb.disabled = true
        })
      }
    })

    this.syncSelectAll(idx)
  }

  hideBranches(idx) {
    const branchLists = this.branchListTargets.filter(
      (el) => el.dataset.roleIndex === idx,
    )
    branchLists.forEach((bl) => {
      bl.classList.add("hidden")
      bl.querySelectorAll("input[type=checkbox]").forEach((cb) => {
        cb.disabled = true
        cb.checked = false
      })
    })

    const selectAll = this.selectAllCheckboxTargets.find(
      (el) => el.dataset.roleIndex === idx,
    )
    if (selectAll) {
      selectAll.checked = false
      selectAll.disabled = true
    }
  }

  syncSelectAll(idx) {
    const selectAll = this.selectAllCheckboxTargets.find(
      (el) => el.dataset.roleIndex === idx,
    )
    if (!selectAll) return

    const visibleCheckboxes = this.branchCheckboxTargets.filter(
      (el) => el.dataset.roleIndex === idx && !el.closest(".hidden"),
    )

    if (visibleCheckboxes.length === 0) {
      selectAll.checked = false
      selectAll.disabled = true
      return
    }

    selectAll.disabled = false
    selectAll.checked = visibleCheckboxes.every((cb) => cb.checked)
  }
}
